<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
} from '@headlessui/vue'
import { type NavigationNode } from '~/layouts/navigation'
import { useCommandHistory } from '~/composables/useCommandHistory'
import type { CommandPaletteDialog } from '~/.nuxt/components'

export interface CommandItem {
  id: string
  name: string
  description: string
  icon?: string
  to?: string
  action?: () => void
}

const props = defineProps<{
  navigationItems: NavigationNode[]
}>()

const router = useRouter()
const query = ref('')
const selectedItem = ref<CommandItem | null>(null)

const { addToHistory } = useCommandHistory()

const commandPaletteDialog = ref<InstanceType<typeof CommandPaletteDialog> | null>(null)

const mode = computed(() => {
  const name = query.value.toLowerCase()
  if (name.startsWith('product ')) {
    return 'product'
  } 
  if (name.startsWith('order ')) {
    return 'order'
  } 
  if (name.length < 8 && Number.isInteger(parseInt(name.trim()))) {
    return 'shortcuts'
  }
  return 'navigation'
})

const args = computed(() => mode.value === 'navigation' || mode.value === 'shortcuts' ? query.value : query.value.split(' ').slice(1).join(' '))

// Handle item selection
const onSelect = (item: CommandItem) => {
  if (item.action) {
    item.action()
  } else if (item.to) {
    router.push({ name: item.to })
  }

  addToHistory(item)
  commandPaletteDialog.value?.close()
}
const help = `Search help:

anything - navigate to pages
product [name/ref] - find products
order [ref] - find orders
[number] - go to ID shortcuts
`
</script>

<template>
  <CommandPaletteDialog ref="commandPaletteDialog" @opened="query = ''">
    <Combobox v-model="selectedItem" @update:model-value="onSelect">
      <div class="relative">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
        >
          <div
            class="i-heroicons-solid-search h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </div>
        <ComboboxInput
          class="w-full border-0 bg-transparent pl-10 pr-4 py-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 text-lg"
          placeholder="Search navigation, 'product abc', or 'order xyz'..."
          @change="query = $event.target.value"
        />
        <div
          v-if="!query.length"
          class="absolute top-4 right-4 i-heroicons-solid-question-mark-circle icon !text-gray-600"
          v-tooltip.top="{
            value: help,
            pt: {
              text: 'text-xs !w-64'
            }
          }"
          aria-hidden="true"
        />
      </div>

      <div class="border-t">
        <ComboboxOptions class="max-h-[60vh] overflow-y-auto" static>
          <CommandPaletteHistory v-if="!query.length" />
          <CommandPaletteNavigation v-if="mode === 'navigation'" :query="args" :navigation-items="navigationItems" />
          <CommandPaletteOrders v-else-if="mode === 'order'" :query="args" />
          <CommandPaletteProducts v-else-if="mode === 'product'" :query="args" />
          <CommandPaletteShortcuts v-else-if="mode === 'shortcuts'" :query="args" />
        </ComboboxOptions>
      </div>
    </Combobox>
  </CommandPaletteDialog>
</template>